import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  FormGroup,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  LocationOn as LocationOnIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { api } from '../services/api';

interface Question {
  id: string;
  type: 'text' | 'number' | 'radio' | 'checkbox' | 'select';
  label: string;
  required: boolean;
  options?: string[];
  conditional?: {
    questionId: string;
    value: string;
  };
}

interface Survey {
  id: string;
  title: string;
  description: string;
  questions: Question[];
  requiresAgentCode: boolean;
  expiresAt?: string;
}

const SurveyPublic: React.FC = () => {
  const { surveyId, token } = useParams<{ surveyId: string; token?: string }>();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [agentCode, setAgentCode] = useState('');
  const [agentCodeVerified, setAgentCodeVerified] = useState(false);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [visibleQuestions, setVisibleQuestions] = useState<Set<string>>(new Set());
  const [agentCodeInput, setAgentCodeInput] = useState(''); // Code agent saisi dans le formulaire

  useEffect(() => {
    loadSurvey();
    setStartTime(new Date());
  }, []);

  useEffect(() => {
    if (survey) {
      updateVisibleQuestions();
    }
  }, [answers, survey]);

  const loadSurvey = async () => {
    try {
      setLoading(true);
      // Charger le formulaire depuis Supabase
      if (surveyId) {
        const { forms } = await api.forms.getAll();
        const form = forms.find(f => f.id === surveyId);
        if (form) {
          // Convertir le form_schema.fields en questions
          const formSchema = form.form_schema as any;
          const fields = formSchema?.fields || [];
          
          const questions: Question[] = fields.map((field: any, index: number) => ({
            id: field.name || `q${index + 1}`,
            type: field.type === 'select' ? 'radio' : (field.type === 'checkbox' ? 'checkbox' : field.type),
            label: field.label || field.name,
            required: field.required || false,
            options: field.options || undefined,
            placeholder: field.placeholder || undefined,
          }));

          const surveyData: Survey = {
            id: form.id,
            title: form.name,
            description: form.description || '',
            questions,
            requiresAgentCode: false,
            // Ne pas définir expiresAt si le formulaire n'a pas de date d'expiration
            expiresAt: undefined,
          };

          // Vérifier l'expiration seulement si une date d'expiration existe
          if (surveyData.expiresAt && new Date(surveyData.expiresAt) < new Date()) {
            setLocationError('Ce questionnaire a expiré.');
            setLoading(false);
            return;
          }

          setSurvey(surveyData);
          setVisibleQuestions(new Set([questions[0]?.id].filter(Boolean)));
          setLoading(false);
          return;
        }
      }
      
      // Fallback: sondage par défaut si aucun formulaire trouvé
      const mockSurvey: Survey = {
        id: surveyId || '1',
        title: 'Enquête Consommateurs - Marchés Cotonou',
        description: 'Cette enquête nous aide à mieux comprendre vos habitudes de consommation',
        requiresAgentCode: token === 'secure',
        expiresAt: undefined, // Pas de date d'expiration par défaut
        questions: [
          {
            id: 'q1',
            type: 'number',
            label: 'Quel est votre âge ?',
            required: true,
          },
          {
            id: 'q2',
            type: 'radio',
            label: 'Dans quel quartier résidez-vous ?',
            required: true,
            options: ['Dantokpa', 'Ganhi', 'Fidjrossè', 'Akpakpa', 'Autre'],
          },
          {
            id: 'q3',
            type: 'radio',
            label: 'Avez-vous déjà acheté un produit SOBEBRA ?',
            required: true,
            options: ['Oui', 'Non'],
          },
          {
            id: 'q4',
            type: 'radio',
            label: 'Quelle est votre fréquence d\'achat ?',
            required: false,
            options: ['Quotidien', 'Hebdomadaire', 'Mensuel', 'Occasionnel'],
            conditional: {
              questionId: 'q3',
              value: 'Oui',
            },
          },
          {
            id: 'q5',
            type: 'checkbox',
            label: 'Quels types de produits préférez-vous ?',
            required: false,
            options: ['Boissons', 'Snacks', 'Produits laitiers', 'Autre'],
            conditional: {
              questionId: 'q3',
              value: 'Oui',
            },
          },
          {
            id: 'q6',
            type: 'text',
            label: 'Avez-vous des suggestions ou commentaires ?',
            required: false,
          },
        ],
      };

      // Vérifier l'expiration seulement si une date d'expiration existe
      if (mockSurvey.expiresAt && new Date(mockSurvey.expiresAt) < new Date()) {
        setLocationError('Ce questionnaire a expiré.');
        setLoading(false);
        return;
      }

      setSurvey(mockSurvey);
      setVisibleQuestions(new Set([mockSurvey.questions[0].id]));
    } catch (error) {
      console.error('Erreur lors du chargement du sondage:', error);
      setLocationError('Erreur lors du chargement du questionnaire.');
    } finally {
      setLoading(false);
    }
  };

  const updateVisibleQuestions = () => {
    if (!survey) return;

    const visible = new Set<string>();
    survey.questions.forEach((question, index) => {
      if (index === 0) {
        visible.add(question.id);
      } else {
        const prevQuestion = survey.questions[index - 1];
        const prevAnswer = answers[prevQuestion.id];

        if (!question.conditional) {
          visible.add(question.id);
        } else {
          const conditional = question.conditional;
          const conditionalAnswer = answers[conditional.questionId];
          if (conditionalAnswer === conditional.value) {
            visible.add(question.id);
          }
        }
      }
    });

    setVisibleQuestions(visible);
  };

  const handleVerifyAgentCode = () => {
    // Simuler la vérification du code agent
    if (agentCode.length >= 4) {
      setAgentCodeVerified(true);
    } else {
      setLocationError('Code agent invalide. Le code doit contenir au moins 4 caractères.');
    }
  };

  const handleAnswerChange = (questionId: string, value: any) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleNext = () => {
    const currentQuestion = survey?.questions[currentStep];
    if (currentQuestion?.required && !answers[currentQuestion.id]) {
      setLocationError('Cette question est obligatoire.');
      return;
    }

    if (currentStep < (survey?.questions.length || 0) - 1) {
      setCurrentStep(currentStep + 1);
      setLocationError(null);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setLocationError(null);
    }
  };


  const reverseGeocode = async (lat: number, lng: number): Promise<string> => {
    try {
      // Vérifier que les coordonnées sont valides (Bénin approximativement: lat 6-12, lng 1-4)
      if (lat < 5 || lat > 13 || lng < 0 || lng > 5) {
        return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      }

      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1&accept-language=fr`,
        {
          headers: {
            'User-Agent': 'SOBEBRA-Zenith Platform',
          },
        }
      );
      const data = await response.json();
      if (data.display_name) {
        // Construire une adresse précise
        let addressParts: string[] = [];
        if (data.address) {
          if (data.address.house_number) addressParts.push(data.address.house_number);
          if (data.address.road) addressParts.push(data.address.road);
          if (data.address.neighbourhood || data.address.suburb || data.address.quarter) {
            addressParts.push(data.address.neighbourhood || data.address.suburb || data.address.quarter);
          }
          if (data.address.city || data.address.town || data.address.municipality) {
            addressParts.push(data.address.city || data.address.town || data.address.municipality);
          }
        }
        
        let finalAddress: string;
        if (addressParts.length > 0) {
          finalAddress = addressParts.join(', ');
        } else {
          finalAddress = data.display_name || '';
        }
        
        // Toujours inclure les coordonnées GPS exactes
        const coordsStr = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        if (finalAddress && finalAddress.length > 5) {
          return `${finalAddress} (${coordsStr})`;
        } else {
          return coordsStr;
        }
      }
    } catch (error) {
      console.error('Erreur lors du reverse geocoding:', error);
    }
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  };

  const requestLocation = (): Promise<{ lat: number; lng: number; address?: string }> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('La géolocalisation n\'est pas supportée par votre navigateur. Veuillez utiliser un navigateur moderne.'));
        return;
      }

      function proceedWithGeolocation() {
        const options: PositionOptions = {
          enableHighAccuracy: true,
          timeout: 20000,
          maximumAge: 0, // Ne pas utiliser de position en cache pour garantir la précision
        };

        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            
            // Vérifier que les coordonnées sont valides
            if (isNaN(lat) || isNaN(lng)) {
              reject(new Error('Les coordonnées GPS obtenues sont invalides. Veuillez réessayer.'));
              return;
            }

            // Vérifier que les coordonnées sont dans une plage raisonnable
            if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
              reject(new Error('Les coordonnées GPS obtenues sont hors limites. Veuillez réessayer.'));
              return;
            }

            // Obtenir l'adresse via reverse geocoding
            try {
              const address = await reverseGeocode(lat, lng);
              resolve({
                lat,
                lng,
                address,
              });
            } catch (geoError) {
              // Si le reverse geocoding échoue, retourner quand même les coordonnées
              resolve({
                lat,
                lng,
                address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
              });
            }
          },
          (error) => {
            console.error('Erreur géolocalisation:', error);
            let errorMessage = '';
            
            // Utiliser les constantes d'erreur de géolocation
            if (error.code === error.PERMISSION_DENIED || error.code === 1) {
              errorMessage = 'L\'accès à la géolocalisation a été refusé. Veuillez autoriser l\'accès à votre position dans les paramètres de votre navigateur et rafraîchir la page.';
            } else if (error.code === error.POSITION_UNAVAILABLE || error.code === 2) {
              errorMessage = 'Les informations de position ne sont pas disponibles. Vérifiez que votre GPS est activé et que vous êtes dans une zone avec couverture réseau.';
            } else if (error.code === error.TIMEOUT || error.code === 3) {
              errorMessage = 'La demande de position a expiré (20 secondes). Veuillez réessayer. Assurez-vous d\'être dans une zone avec bonne réception GPS et que votre GPS est activé.';
            } else {
              // Pour les erreurs inconnues
              errorMessage = `Erreur de géolocalisation. Code: ${error.code || 'inconnu'}. Veuillez vérifier que votre GPS est activé et réessayer.`;
            }
            
            reject(new Error(errorMessage));
          },
          options
        );
      }

      // Vérifier les permissions d'abord (optionnel, peut être ignoré si non supporté)
      if ('permissions' in navigator) {
        navigator.permissions.query({ name: 'geolocation' as PermissionName }).then((result) => {
          if (result.state === 'denied') {
            reject(new Error('L\'accès à la géolocalisation a été refusé. Veuillez autoriser l\'accès dans les paramètres de votre navigateur et rafraîchir la page.'));
            return;
          }
          proceedWithGeolocation();
        }).catch(() => {
          // Si permissions API n'est pas supporté, continuer quand même
          proceedWithGeolocation();
        });
      } else {
        proceedWithGeolocation();
      }
    });
  };

  const handleSubmit = async () => {
    // Vérifier que le code agent est renseigné
    if (!agentCodeInput || agentCodeInput.trim().length < 3) {
      setLocationError('Veuillez renseigner votre code agent (minimum 3 caractères).');
      return;
    }

    const currentQuestion = survey?.questions[currentStep];
    if (currentQuestion?.required && !answers[currentQuestion.id]) {
      setLocationError('Cette question est obligatoire.');
      return;
    }

    // Vérifier que toutes les questions requises sont répondues
    const requiredQuestions = survey?.questions.filter(q => q.required && visibleQuestions.has(q.id)) || [];
    const missingAnswers = requiredQuestions.filter(q => !answers[q.id]);

    if (missingAnswers.length > 0) {
      setLocationError(`Veuillez répondre à toutes les questions obligatoires.`);
      return;
    }

    setSubmitting(true);
    setLocationError(null);

    try {
      // Capturer la géolocalisation (OBLIGATOIRE)
      const locationData = await requestLocation();
      setLocation({ lat: locationData.lat, lng: locationData.lng });

      // Capturer les métadonnées
      const endTime = new Date();
      const duration = startTime ? Math.round((endTime.getTime() - startTime.getTime()) / 1000) : 0;
      const userAgent = navigator.userAgent;
      const deviceType = /Mobile|Android|iPhone|iPad/.test(userAgent) ? 'Mobile' : 'Desktop';

      // Préparer les données de soumission avec l'adresse si disponible
      const submissionData = {
        surveyId: survey?.id,
        answers: {
          ...answers,
          agentCode: agentCodeInput.trim(), // Toujours inclure le code agent
        },
        location: {
          lat: locationData.lat,
          lng: locationData.lng,
          address: locationData.address || `${locationData.lat.toFixed(6)}, ${locationData.lng.toFixed(6)}`,
        },
        metadata: {
          startTime: startTime?.toISOString(),
          endTime: endTime.toISOString(),
          duration,
          userAgent,
          deviceType,
          agentCode: agentCodeInput.trim(), // Code agent dans les métadonnées aussi
        },
      };

      // Envoyer la soumission à Supabase
      if (survey?.id) {
        await api.forms.submit(survey.id, submissionData);
      }

      setSubmitted(true);
      setLocationError(null);
    } catch (error: any) {
      console.error('Erreur lors de la soumission:', error);
      // Afficher l'erreur seulement si c'est vraiment une erreur de géolocalisation
      if (error.message && (
        error.message.includes('géolocalisation') ||
        error.message.includes('position') ||
        error.message.includes('GPS') ||
        error.message.includes('PERMISSION_DENIED') ||
        error.message.includes('POSITION_UNAVAILABLE') ||
        error.message.includes('TIMEOUT')
      )) {
        setLocationError(error.message);
      } else {
        setLocationError('Erreur lors de la soumission. Veuillez réessayer.');
      }
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (locationError && !submitting) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
          p: 2,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          {locationError}
        </Alert>
        <Button onClick={() => navigate('/')} sx={{ mt: 2 }}>
          Retour à l'accueil
        </Button>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
          p: 2,
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 80, color: '#2e7d32', mb: 2 }} />
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Merci !
        </Typography>
        <Typography variant="h6" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Votre réponse a été enregistrée avec succès.
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          Vos données nous aideront à améliorer nos services.
        </Typography>
      </Box>
    );
  }

  // Écran de vérification du code agent
  if (survey?.requiresAgentCode && !agentCodeVerified) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 400, width: '100%' }}>
          <Typography variant="h5" gutterBottom fontWeight="bold" align="center">
            Accès Sécurisé
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Veuillez entrer votre code agent pour accéder au questionnaire
          </Typography>
          <TextField
            fullWidth
            label="Code Agent"
            value={agentCode}
            onChange={(e) => setAgentCode(e.target.value)}
            type="password"
            margin="normal"
            autoFocus
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleVerifyAgentCode();
              }
            }}
          />
          {locationError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {locationError}
            </Alert>
          )}
          <Button
            fullWidth
            variant="contained"
            onClick={handleVerifyAgentCode}
            sx={{ mt: 2 }}
            disabled={!agentCode}
          >
            Vérifier
          </Button>
        </Paper>
      </Box>
    );
  }

  const currentQuestion = survey?.questions[currentStep];
  const progress = survey ? ((currentStep + 1) / survey.questions.length) * 100 : 0;
  const visibleQuestionsList = survey?.questions.filter(q => visibleQuestions.has(q.id)) || [];
  const currentVisibleIndex = visibleQuestionsList.findIndex(q => q.id === currentQuestion?.id) + 1;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f5f5f5',
        pb: 4,
      }}
    >
      {/* En-tête avec progression */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            {survey?.title}
          </Typography>
          <Chip
            label={`${currentVisibleIndex} / ${visibleQuestionsList.length}`}
            color="primary"
            size="small"
          />
        </Box>
        <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Question {currentVisibleIndex} sur {visibleQuestionsList.length}
        </Typography>
      </Paper>

      {/* Contenu principal */}
      <Box sx={{ px: 2 }}>
        <Paper sx={{ p: 3, mb: 2 }}>
          {currentStep === 0 && survey?.description && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {survey.description}
            </Typography>
          )}

          {/* Champ Code Agent - toujours affiché au début */}
          {currentStep === 0 && (
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Code Agent"
                required
                value={agentCodeInput}
                onChange={(e) => setAgentCodeInput(e.target.value)}
                placeholder="Entrez votre code agent unique"
                helperText="Ce code permet d'identifier l'agent qui remplit ce formulaire"
                sx={{ mb: 2 }}
                error={locationError?.includes('code agent') || false}
              />
            </Box>
          )}

          {currentQuestion && (
            <>
              <FormControl fullWidth component="fieldset" sx={{ mb: 2 }}>
                <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'bold' }}>
                  {currentQuestion.label}
                  {currentQuestion.required && (
                    <Typography component="span" color="error">
                      {' '}*
                    </Typography>
                  )}
                </FormLabel>

                {currentQuestion.type === 'text' && (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={answers[currentQuestion.id] || ''}
                    onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                    placeholder="Tapez votre réponse..."
                  />
                )}

                {currentQuestion.type === 'number' && (
                  <TextField
                    fullWidth
                    type="number"
                    value={answers[currentQuestion.id] || ''}
                    onChange={(e) => handleAnswerChange(currentQuestion.id, parseInt(e.target.value) || '')}
                    placeholder="Entrez un nombre"
                  />
                )}

                {currentQuestion.type === 'radio' && (
                  <RadioGroup
                    value={answers[currentQuestion.id] || ''}
                    onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                  >
                    {currentQuestion.options?.map((option) => (
                      <FormControlLabel
                        key={option}
                        value={option}
                        control={<Radio />}
                        label={option}
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </RadioGroup>
                )}

                {currentQuestion.type === 'checkbox' && (
                  <FormGroup>
                    {currentQuestion.options?.map((option) => (
                      <FormControlLabel
                        key={option}
                        control={
                          <Checkbox
                            checked={(answers[currentQuestion.id] || []).includes(option)}
                            onChange={(e) => {
                              const current = answers[currentQuestion.id] || [];
                              if (e.target.checked) {
                                handleAnswerChange(currentQuestion.id, [...current, option]);
                              } else {
                                handleAnswerChange(currentQuestion.id, current.filter((v: string) => v !== option));
                              }
                            }}
                          />
                        }
                        label={option}
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </FormGroup>
                )}
              </FormControl>

              {locationError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {locationError}
                </Alert>
              )}
            </>
          )}
        </Paper>

        {/* Boutons de navigation */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {currentStep > 0 && (
            <Button
              fullWidth
              variant="outlined"
              onClick={handlePrevious}
              sx={{ py: 1.5 }}
            >
              Précédent
            </Button>
          )}
          {currentStep < visibleQuestionsList.length - 1 ? (
            <Button
              fullWidth
              variant="contained"
              onClick={handleNext}
              sx={{ py: 1.5 }}
            >
              Suivant
            </Button>
          ) : (
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : <LocationOnIcon />}
              sx={{ py: 1.5 }}
            >
              {submitting ? 'Envoi en cours...' : 'Soumettre avec géolocalisation'}
            </Button>
          )}
        </Box>

        {/* Info géolocalisation */}
        {!submitting && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <LocationOnIcon sx={{ mr: 1 }} />
            Votre position sera capturée automatiquement lors de la soumission
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default SurveyPublic;



